import React, {
  ChangeEvent,
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {
  Card,
  Grid,
  InputAdornment,
  TextField,
  SvgIcon,
  Box,
  CardContent,
  Button
} from '@material-ui/core';
import { DatePickerRangeComponent } from 'src/components';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { dateToday, transformerDateField } from 'src/utils';
import { CategoryListDropdown } from 'src/components/filters/CategoryDropdown';

import SearchIcon from '@material-ui/icons/Search';
import GetAppIcon from '@material-ui/icons/GetApp';
import { BranchListDropDown } from 'src/components/dropdown';
import { ManufacturerDropDownV2 } from 'src/components/dropdown/ManufacturerDropdownV2';
import { useLocation, useNavigate } from 'react-router';
import useSellOut from 'src/hooks/use-sell-out';
import { slices, useAppSelector } from 'src/redux';

const { selectors: categorySelectors } = slices.category;

interface Props {}

const Component: FC<Props> = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const {
    sellOutList,
    getSellOutList,
    setFilter,
    getSellOutSummary,
    getSellOutNvidiaCSV
  } = useSellOut();
  const categories = useAppSelector(categorySelectors.selectCategories);
  const queryParams = new URLSearchParams(search);

  const [filters, setLocalFilters] = useState<{
    keyword?: string;
    start_date?: string;
    end_date?: string;
    category_ids?: number[];
    page?: number;
    manufacturer_ids?: number[];
    branch_ids?: number[];
  }>({
    keyword: '',
    start_date: '',
    end_date: '',
    category_ids: [],
    page: 1,
    manufacturer_ids: []
  });

  const init_category = useMemo(
    () =>
      categories?.find(
        (categ) => categ?.id?.toString() === queryParams?.get('category_ids')
      ),
    [queryParams, categories]
  );

  const init_manufacturers = useMemo(() => {
    const manufacturer_ids = queryParams.get('manufacturer_ids');
    if (!manufacturer_ids) return [];
    return decodeURIComponent(manufacturer_ids)
      .split(',')
      .map(Number)
      .filter(Boolean);
  }, [queryParams]);

  const onChangedDate = useCallback(
    (date: MaterialUiPickersDate, field: 'from' | 'to') => {
      const transformedDate = date ? transformerDateField(date) : undefined;

      if (field === 'to') {
        setLocalFilters((prev) => ({
          ...prev,
          end_date: transformedDate || undefined
        }));
      }
      if (field === 'from') {
        setLocalFilters((prev) => ({
          ...prev,
          start_date: transformedDate || undefined
        }));
      }
    },
    []
  );

  const onChangeKeyword = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setLocalFilters((prev) => ({ ...prev, keyword: value }));
  };

  const onHandleApplyFilter = (filter: any) => {
    const params = new URLSearchParams();

    if (filter.keyword) params.set('keyword', filter.keyword);
    if (filter.start_date) params.set('start_date', filter.start_date);
    if (filter.end_date) params.set('end_date', filter.end_date);
    if (filter.category_ids)
      params.set('category_ids', filter.category_ids.join(','));
    params.set('page', filter.page || '1');

    if (filters?.manufacturer_ids?.length)
      params.set('manufacturer_ids', filters.manufacturer_ids.join(','));

    if (filters?.branch_ids?.length)
      params.set('branch_ids', filters.branch_ids.join(','));

    setFilter(filter);
    getSellOutSummary(filter);
    if (sellOutList && sellOutList?.length > 0) {
      getSellOutList(filter);
    }

    navigate(`?${params}`);
  };

  const initializeFilters = useCallback(() => {
    const start_date = queryParams.get('start_date') || null;
    const end_date = queryParams.get('end_date') || null;
    const keyword = queryParams.get('keyword') || null;
    const page = queryParams.get('page') || 1;
    const category_ids = queryParams.get('category_ids')
      ? queryParams
          .get('category_ids')
          ?.split(',')
          .map(Number)
      : null;
    const branch_ids = queryParams.get('branch_ids')
      ? queryParams
          .get('branch_ids')
          ?.split(',')
          .map(Number)
      : null;

    const manufacturer_ids = queryParams.get('manufacturer_ids')
      ? queryParams
          .get('manufacturer_ids')
          ?.split(',')
          .map(Number)
      : null;

    //global state
    setFilter({
      start_date: start_date || '',
      end_date: end_date || '',
      keyword: keyword || '',
      category_ids: category_ids || [],
      branch_ids: branch_ids || [],
      manufacturer_ids: manufacturer_ids || [],
      page: Number(page)
    });

    //local stage
    setLocalFilters(() => ({
      start_date: start_date || '',
      end_date: end_date || '',
      keyword: keyword || '',
      category_ids: category_ids || [],
      branch_ids: branch_ids || [],
      manufacturer_ids: manufacturer_ids || [],
      page: Number(page) || 1
    }));

    if (queryParams.get('start_date') && queryParams.get('end_date')) {
      getSellOutSummary({
        start_date: start_date || '',
        end_date: end_date || '',
        keyword: keyword || '',
        category_ids: category_ids || [],
        branch_ids: branch_ids || [],
        manufacturer_ids: manufacturer_ids || []
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const onDownloadNvidiaSellOut = async () => {
    try {
      getSellOutNvidiaCSV(filters);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    initializeFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card style={{ padding: 10, marginBottom: 20 }}>
      <CardContent>
        <Box style={{ padding: 3 }}>
          <Grid
            container
            spacing={3}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Grid
              item
              xs={12}
              md={12}
              style={{ display: 'grid', placeContent: 'center' }}
            >
              <DatePickerRangeComponent
                title="Date Sold"
                fromDateValue={filters.start_date || null}
                toDateValue={filters.end_date || null}
                dateToLabel="End Date"
                dateFromLabel="Start Date"
                toDateMin={filters.start_date}
                fromDateMax={dateToday()}
                onChangeToDate={(date) => onChangedDate(date, 'to')}
                onChangeFromDate={(date) => onChangedDate(date, 'from')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search Product"
                variant="outlined"
                value={filters.keyword || ''}
                onChange={(e: any) => onChangeKeyword(e)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SvgIcon fontSize="small" color="action">
                        <SearchIcon />
                      </SvgIcon>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <CategoryListDropdown
                defaultValue={init_category}
                onHandleCategories={(categoryId: number) => {
                  setLocalFilters((prev) => ({
                    ...prev,
                    category_ids: [categoryId]
                  }));
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <BranchListDropDown
                defaultValue={
                  filters?.branch_ids ||
                  queryParams
                    ?.get('branch_ids')
                    ?.split(',')
                    .map(Number)
                }
                isElevated={false}
                paperSpace={0}
                multiple
                onHandleBranchChange={(branchIds?: number[]) => {
                  setLocalFilters((prev) => ({
                    ...prev,
                    branch_ids: branchIds
                  }));
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ManufacturerDropDownV2
                initialValue={init_manufacturers}
                isMultiple={true}
                label="Included Manufacturers(optional)"
                onHandleManufacturerSetValue={(manufacturer_ids?: number[]) => {
                  setLocalFilters((prev) => ({
                    ...prev,
                    manufacturer_ids: manufacturer_ids
                  }));
                }}
              />
            </Grid>
            <Grid item xs={12} md={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  endIcon={<GetAppIcon />}
                  onClick={onDownloadNvidiaSellOut}
                  color="primary"
                  variant="text"
                  size="small"
                >
                  Nvidia Sell Out
                </Button>

                <Button
                  endIcon={<SearchIcon />}
                  onClick={() => onHandleApplyFilter(filters)}
                  color="primary"
                  variant="contained"
                >
                  Apply Filter
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};

export const SellOutFilters = memo(Component);
